(() => {
  if (window.SakuraProductReviewsFallback) {
    window.SakuraProductReviewsFallback.initialize();
    return;
  }

  const API_BASE = 'https://wise-reviews.codexapps.co/api/proxy/reviews';
  const WAIT_MS = 6500;
  const FETCH_TIMEOUT_MS = 12000;
  const NATIVE_POLL_MS = 500;
  const REVIEWS_PER_VIEW = 6;
  const FILTER_PAGE_SIZE = 150;
  const MAX_FILTER_PAGES = 20;
  const SORT_OPTIONS = [
    ['recommended', 'Recommended'],
    ['newest', 'Newest'],
    ['oldest', 'Oldest'],
    ['highest', 'Highest rating'],
    ['lowest', 'Lowest rating']
  ];
  const RATING_FILTERS = [0, 5, 4, 3, 2, 1];
  const controllers = new WeakMap();

  const controllerIsActive = (section, controller) => controller.active && controllers.get(section) === controller;

  const requireActiveController = (section, controller) => {
    if (!controllerIsActive(section, controller)) throw new Error('Stale review controller.');
  };

  const canonicalPositiveId = (value) => {
    if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
    if (typeof value !== 'string' || value !== value.trim() || !/^[1-9]\d*$/.test(value)) return null;
    return value;
  };

  const makeElement = (tagName, className, text) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = String(text);
    return element;
  };

  const nativeFeedHasContent = (section) => {
    const native = section.querySelector('wise-product-reviews');
    if (!native || native.querySelector('svg[role="status"]')) return false;
    const shell = native.querySelector('.wise-reviews-element');
    return Boolean(shell && shell.querySelector('select') && shell.textContent.trim());
  };

  const nativeMount = (section) => section.querySelector('[data-sakura-product-review-native]');
  const fallbackMount = (section) => section.querySelector('[data-sakura-product-review-fallback]');
  const fallbackOutput = (section) => section.querySelector('[data-sakura-product-review-fallback-output]');
  const usesFilters = (section) => section.dataset.reviewControls === 'filters';

  const setNativeVisibility = (section, hidden) => {
    const mount = nativeMount(section);
    if (!mount) return;
    if (hidden) {
      mount.setAttribute('aria-hidden', 'true');
      mount.setAttribute('inert', '');
    } else {
      mount.removeAttribute('aria-hidden');
      mount.removeAttribute('inert');
    }
  };

  const clearFallback = (section, restoreFocus = false) => {
    const output = fallbackOutput(section);
    const focusWasInFallback = Boolean(restoreFocus || (output && output.contains(document.activeElement)));
    if (output) output.remove();
    setNativeVisibility(section, false);
    section.dataset.sakuraProductReviewState = 'native';
    if (focusWasInFallback) {
      const mount = nativeMount(section);
      if (!mount) return;
      const focusTarget = mount.querySelector('button, select, input, textarea, a[href], [tabindex]:not([tabindex="-1"])') || mount;
      if (focusTarget === mount) mount.setAttribute('tabindex', '-1');
      focusTarget.focus({ preventScroll: true });
    }
  };

  const replaceFallback = (section, output, state) => {
    const mount = fallbackMount(section);
    if (!mount) return;
    const existing = fallbackOutput(section);
    if (existing) existing.remove();
    output.setAttribute('data-sakura-product-review-fallback-output', '');
    mount.append(output);
    setNativeVisibility(section, true);
    section.dataset.sakuraProductReviewState = state;
  };

  const stopController = (section, abortRequest = true) => {
    const controller = controllers.get(section);
    if (!controller) return;
    controller.active = false;
    window.clearTimeout(controller.timer);
    if (controller.poll) window.clearInterval(controller.poll);
    if (controller.observer) controller.observer.disconnect();
    if (abortRequest && controller.request) controller.request.abort();
  };

  const settleNative = (section) => {
    const controller = controllers.get(section);
    clearFallback(section, Boolean(controller && controller.restoreFocusOnNative));
    if (controller) controller.restoreFocusOnNative = false;
    stopController(section);
  };

  const renderStatus = (section, message) => {
    replaceFallback(section, makeElement('p', 'sakura-review-feed__status', message), 'unavailable');
  };

  const renderStars = (rating) => {
    const safeRating = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    const stars = makeElement('span', 'sakura-review-feed__stars', '★'.repeat(safeRating));
    stars.setAttribute('aria-label', `${safeRating} out of 5 stars`);
    return stars;
  };

  const renderReviewCard = (review) => {
    const card = makeElement('article', 'sakura-review-feed__card');
    card.setAttribute('role', 'listitem');
    card.append(renderStars(review.rating));
    if (review.title) card.append(makeElement('h3', 'sakura-review-feed__card-title', review.title));
    if (review.body) card.append(makeElement('p', 'sakura-review-feed__body', review.body));

    const byline = makeElement('p', 'sakura-review-feed__byline');
    if (review.reviewer_name) byline.append(makeElement('strong', '', review.reviewer_name));
    if (review.verified) byline.append(makeElement('span', 'sakura-review-feed__verified', 'Verified'));
    if (review.date) byline.append(makeElement('time', '', review.date));
    if (byline.children.length) card.append(byline);
    return card;
  };

  const buildEndpoint = (section, page) => {
    const endpoint = new URL(API_BASE);
    endpoint.searchParams.set('shop', section.dataset.reviewsShop || '');
    endpoint.searchParams.set('product_id', section.dataset.reviewsProductId || '');
    endpoint.searchParams.set('sort_by', 'RECOMMENDED');
    endpoint.searchParams.set('type', 'product_reviews');
    if (usesFilters(section)) endpoint.searchParams.set('itemsPerPage', '150');
    else endpoint.searchParams.set('itemsPerPage', '6');
    endpoint.searchParams.set('deviceType', window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop');
    endpoint.searchParams.set('preview', 'false');
    endpoint.searchParams.set('page', String(page));
    return endpoint;
  };

  const fetchPayload = async (section, controller, page) => {
    requireActiveController(section, controller);
    const request = new AbortController();
    controller.request = request;
    const timeout = window.setTimeout(() => request.abort(), FETCH_TIMEOUT_MS);
    try {
      const response = await fetch(buildEndpoint(section, page), {
        headers: { Accept: 'application/json' },
        signal: request.signal
      });
      if (!response.ok) throw new Error(`WiseReviews request failed: ${response.status}`);
      const payload = await response.json();
      requireActiveController(section, controller);
      return payload;
    } finally {
      window.clearTimeout(timeout);
      if (controller.request === request) controller.request = null;
    }
  };

  const validateProductReviewPage = (section, payload, page, expectedMeta = null) => {
    if (!payload || !Array.isArray(payload.data) || !payload.meta) throw new Error('Invalid review response.');
    const { current_page: currentPage, last_page: lastPage, total, per_page: perPage } = payload.meta;
    if (!Number.isInteger(currentPage) || currentPage !== page) throw new Error('Invalid review page number.');
    if (!Number.isInteger(lastPage) || lastPage < 1 || lastPage > MAX_FILTER_PAGES) throw new Error('Invalid review page count.');
    if (!Number.isInteger(total) || total < 0 || total > FILTER_PAGE_SIZE * MAX_FILTER_PAGES) throw new Error('Invalid review total.');
    if (perPage !== FILTER_PAGE_SIZE) throw new Error('Invalid review page size.');
    const calculatedLastPage = Math.max(1, Math.ceil(total / FILTER_PAGE_SIZE));
    if (lastPage !== calculatedLastPage) throw new Error('Inconsistent review page count.');
    if (expectedMeta && (lastPage !== expectedMeta.lastPage || total !== expectedMeta.total)) throw new Error('Review metadata changed during paging.');
    const expectedLength = total === 0 ? 0 : (page < lastPage ? FILTER_PAGE_SIZE : total - (lastPage - 1) * FILTER_PAGE_SIZE);
    if (payload.data.length !== expectedLength) throw new Error('Incomplete review page.');
    const productId = canonicalPositiveId(section.dataset.reviewsProductId || '');
    if (!productId) throw new Error('Review product scope is invalid.');
    payload.data.forEach((review) => {
      if (!review || !canonicalPositiveId(review.id)) throw new Error('Review identity is missing.');
      if (canonicalPositiveId(review.product_id) !== productId) throw new Error('Review product scope does not match.');
      const { rating } = review;
      if (!Number.isInteger(rating) || rating < 1 || rating > 5) throw new Error('Review rating is invalid.');
    });
    return { lastPage, total };
  };

  const fetchCompleteProductPayload = async (section, controller) => {
    const first = await fetchPayload(section, controller, 1);
    requireActiveController(section, controller);
    const expectedMeta = validateProductReviewPage(section, first, 1);
    const combined = first.data.slice();
    const seenIds = new Set(combined.map((review) => canonicalPositiveId(review.id)));
    const { lastPage, total } = expectedMeta;
    for (let page = 2; page <= lastPage; page += 1) {
      const payload = await fetchPayload(section, controller, page);
      requireActiveController(section, controller);
      validateProductReviewPage(section, payload, page, expectedMeta);
      payload.data.forEach((review) => {
        const id = canonicalPositiveId(review.id);
        if (seenIds.has(id)) throw new Error('Duplicate review identity.');
        seenIds.add(id);
        combined.push(review);
      });
    }
    if (combined.length !== total || seenIds.size !== total) throw new Error('Incomplete review result.');
    return { ...first, data: combined, meta: { ...(first.meta || {}), current_page: 1, last_page: 1 } };
  };

  const reviewTimestamp = (review) => {
    const timestamp = Date.parse(review.date_raw || review.created_at || review.date || '');
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const compareNewest = (left, right) => reviewTimestamp(right) - reviewTimestamp(left) || left.__sourceIndex - right.__sourceIndex;

  const prepareReviews = (controller) => {
    const filtered = controller.reviews.filter((review) => !controller.rating || Number(review.rating) === controller.rating);
    const sorted = filtered.slice();
    if (controller.sort === 'newest') sorted.sort(compareNewest);
    else if (controller.sort === 'oldest') sorted.sort((left, right) => reviewTimestamp(left) - reviewTimestamp(right) || left.__sourceIndex - right.__sourceIndex);
    else if (controller.sort === 'highest') sorted.sort((left, right) => Number(right.rating) - Number(left.rating) || compareNewest(left, right));
    else if (controller.sort === 'lowest') sorted.sort((left, right) => Number(left.rating) - Number(right.rating) || compareNewest(left, right));
    else sorted.sort((left, right) => left.__sourceIndex - right.__sourceIndex);
    return sorted;
  };

  const appendSummary = (output, controller) => {
    const summary = makeElement('div', 'sakura-review-feed__summary');
    const rawAverage = controller.averageRating;
    const average = rawAverage === null || rawAverage === '' ? NaN : Number(rawAverage);
    const hasAverage = Number.isFinite(average) && average >= 0 && average <= 5;
    const score = hasAverage ? average.toFixed(1) : '—';
    summary.append(makeElement('strong', 'sakura-review-feed__score', score));
    if (hasAverage) summary.append(renderStars(average));
    summary.append(makeElement('span', 'sakura-review-feed__count', `${controller.total} reviews`));
    output.append(summary);
  };

  const renderFilterControls = (section, controller, output, filteredCount) => {
    const controls = makeElement('div', 'sakura-review-feed__controls');
    const sortLabel = makeElement('label', 'sakura-review-feed__sort');
    const selectId = `SakuraReviewSort-${section.id || 'feed'}`;
    sortLabel.setAttribute('for', selectId);
    sortLabel.append(makeElement('span', '', 'Sort reviews'));
    const select = makeElement('select', 'sakura-review-feed__sort-select');
    select.id = selectId;
    SORT_OPTIONS.forEach(([value, label]) => {
      const option = makeElement('option', '', label);
      option.value = value;
      if (value === controller.sort) option.selected = true;
      select.append(option);
    });
    select.value = controller.sort;
    select.addEventListener('change', () => {
      controller.sort = select.value;
      controller.viewPage = 1;
      renderFilteredView(section, controller, 'sort');
    });
    sortLabel.append(select);
    controls.append(sortLabel);

    const ratingGroup = makeElement('div', 'sakura-review-feed__rating-group');
    ratingGroup.setAttribute('role', 'group');
    ratingGroup.setAttribute('aria-label', 'Filter reviews by star rating');
    RATING_FILTERS.forEach((rating) => {
      const label = rating ? `${rating} star${rating === 1 ? '' : 's'}` : 'All ratings';
      const button = makeElement('button', 'sakura-review-feed__rating-filter', label);
      button.type = 'button';
      button.dataset.rating = String(rating);
      button.setAttribute('aria-pressed', String(controller.rating === rating));
      button.addEventListener('click', () => {
        controller.rating = rating;
        controller.viewPage = 1;
        renderFilteredView(section, controller, `rating:${rating}`);
      });
      ratingGroup.append(button);
    });
    controls.append(ratingGroup);

    const count = makeElement('p', 'sakura-review-feed__filter-count', `${filteredCount} matching review${filteredCount === 1 ? '' : 's'}`);
    count.setAttribute('aria-live', 'polite');
    controls.append(count);
    output.append(controls);
  };

  const restoreFilteredFocus = (output, focusTarget) => {
    if (!focusTarget) return;
    if (focusTarget === 'sort') {
      const select = output.querySelector('.sakura-review-feed__sort-select');
      if (select) select.focus({ preventScroll: true });
      return;
    }
    if (focusTarget.startsWith('rating:')) {
      const rating = focusTarget.split(':')[1];
      const button = Array.from(output.querySelectorAll('.sakura-review-feed__rating-filter')).find((candidate) => candidate.dataset.rating === rating);
      if (button) button.focus({ preventScroll: true });
      return;
    }
    if (focusTarget === 'first-review') {
      const card = output.querySelector('.sakura-review-feed__card');
      if (card) {
        card.setAttribute('tabindex', '-1');
        card.focus({ preventScroll: true });
      }
    }
  };

  const renderFilteredView = (section, controller, focusTarget = '') => {
    const reviews = prepareReviews(controller);
    const totalPages = Math.max(1, Math.ceil(reviews.length / REVIEWS_PER_VIEW));
    controller.viewPage = Math.min(Math.max(1, controller.viewPage), totalPages);
    const start = (controller.viewPage - 1) * REVIEWS_PER_VIEW;
    const visible = reviews.slice(start, start + REVIEWS_PER_VIEW);

    const output = makeElement('div', 'sakura-review-feed__fallback');
    appendSummary(output, controller);
    renderFilterControls(section, controller, output, reviews.length);

    if (visible.length) {
      const list = makeElement('div', 'sakura-review-feed__grid');
      list.setAttribute('role', 'list');
      visible.forEach((review) => list.append(renderReviewCard(review)));
      output.append(list);
    } else {
      output.append(makeElement('p', 'sakura-review-feed__status', 'No reviews match this star rating yet.'));
    }

    const pagination = makeElement('nav', 'sakura-review-feed__pagination');
    pagination.setAttribute('aria-label', 'Review pages');
    const previous = makeElement('button', 'sakura-review-feed__previous btn btn-secondary', 'Previous');
    previous.type = 'button';
    previous.disabled = controller.viewPage <= 1;
    previous.addEventListener('click', () => {
      controller.viewPage -= 1;
      renderFilteredView(section, controller, 'first-review');
    });
    const pageStatus = makeElement('p', 'sakura-review-feed__page-status', `Page ${controller.viewPage} of ${totalPages}`);
    const next = makeElement('button', 'sakura-review-feed__next btn btn-secondary', 'Next');
    next.type = 'button';
    next.disabled = controller.viewPage >= totalPages;
    next.addEventListener('click', () => {
      controller.viewPage += 1;
      renderFilteredView(section, controller, 'first-review');
    });
    pagination.append(previous, pageStatus, next);
    output.append(pagination);

    replaceFallback(section, output, 'loaded');
    restoreFilteredFocus(output, focusTarget);
  };

  const renderFilteredPayload = (section, controller, payload) => {
    const reviews = Array.isArray(payload.data) ? payload.data : [];
    if (!reviews.length) {
      renderStatus(section, 'No reviews are available yet.');
      return;
    }
    controller.reviews = reviews.map((review, index) => ({ ...review, __sourceIndex: index }));
    controller.averageRating = payload.average_rating;
    controller.total = Number(payload.meta && payload.meta.total) || reviews.length;
    controller.sort = 'recommended';
    controller.rating = 0;
    controller.viewPage = 1;
    renderFilteredView(section, controller);
  };

  const renderPagedPayload = (section, controller, payload, append = false, restorePaginationFocus = false) => {
    const reviews = Array.isArray(payload.data) ? payload.data : [];
    if (!reviews.length) {
      if (append) throw new Error('WiseReviews returned an empty review page.');
      renderStatus(section, 'No reviews are available yet.');
      return;
    }

    let output = fallbackOutput(section);
    let list;
    let focusWasOnMore = false;
    if (!append || !output) {
      output = makeElement('div', 'sakura-review-feed__fallback');
      controller.averageRating = payload.average_rating;
      controller.total = Number(payload.meta && payload.meta.total) || reviews.length;
      appendSummary(output, controller);
      list = makeElement('div', 'sakura-review-feed__grid');
      list.setAttribute('role', 'list');
      output.append(list);
      replaceFallback(section, output, 'loaded');
    } else {
      list = output.querySelector('.sakura-review-feed__grid');
      const oldButton = output.querySelector('.sakura-review-feed__more');
      focusWasOnMore = Boolean(restorePaginationFocus || (oldButton && document.activeElement === oldButton));
      if (oldButton) oldButton.remove();
    }

    let firstNewReview = null;
    reviews.forEach((review) => {
      const card = renderReviewCard(review);
      if (!firstNewReview) firstNewReview = card;
      list.append(card);
    });
    if (focusWasOnMore && firstNewReview) {
      firstNewReview.setAttribute('tabindex', '-1');
      firstNewReview.focus({ preventScroll: true });
    }
    controller.page = Number(payload.meta && payload.meta.current_page) || controller.page;
    const lastPage = Number(payload.meta && payload.meta.last_page) || controller.page;
    if (controller.page < lastPage) {
      const button = makeElement('button', 'sakura-review-feed__more btn btn-secondary', 'Load more reviews');
      button.type = 'button';
      button.addEventListener('click', async () => {
        const restoreFocusAfterLoad = document.activeElement === button;
        controller.restoreFocusOnNative = restoreFocusAfterLoad;
        button.disabled = true;
        button.textContent = 'Loading reviews…';
        try {
          const nextPayload = await fetchPayload(section, controller, controller.page + 1);
          requireActiveController(section, controller);
          if (nativeFeedHasContent(section)) settleNative(section);
          else {
            renderPagedPayload(section, controller, nextPayload, true, restoreFocusAfterLoad);
            controller.restoreFocusOnNative = false;
          }
        } catch (_error) {
          if (!controllerIsActive(section, controller)) return;
          const currentOutput = fallbackOutput(section);
          if (currentOutput && currentOutput.contains(button)) {
            button.disabled = false;
            button.textContent = 'Try loading more reviews again';
            if (restoreFocusAfterLoad) button.focus({ preventScroll: true });
          }
          controller.restoreFocusOnNative = false;
        }
      });
      output.append(button);
    }
  };

  const activateFallback = (section) => {
    if (controllers.has(section)) return;
    const controller = {
      active: true,
      observer: null,
      poll: null,
      request: null,
      timer: null,
      page: 1,
      reviews: [],
      sort: 'recommended',
      rating: 0,
      viewPage: 1,
      total: 0,
      averageRating: null,
      restoreFocusOnNative: false
    };
    controllers.set(section, controller);

    controller.observer = new MutationObserver(() => {
      if (nativeFeedHasContent(section)) settleNative(section);
    });
    controller.observer.observe(nativeMount(section) || section, { childList: true, subtree: true, characterData: true });
    controller.poll = window.setInterval(() => {
      if (nativeFeedHasContent(section)) settleNative(section);
    }, NATIVE_POLL_MS);

    controller.timer = window.setTimeout(async () => {
      if (nativeFeedHasContent(section)) {
        settleNative(section);
        return;
      }
      if (!section.dataset.reviewsShop) {
        renderStatus(section, 'Reviews are temporarily unavailable.');
        return;
      }
      if (usesFilters(section) && !section.dataset.reviewsProductId) {
        renderStatus(section, 'Reviews are temporarily unavailable.');
        return;
      }
      try {
        const payload = usesFilters(section)
          ? await fetchCompleteProductPayload(section, controller)
          : await fetchPayload(section, controller, 1);
        requireActiveController(section, controller);
        if (nativeFeedHasContent(section)) settleNative(section);
        else if (usesFilters(section)) renderFilteredPayload(section, controller, payload);
        else renderPagedPayload(section, controller, payload);
      } catch (_error) {
        if (!controllerIsActive(section, controller)) return;
        if (nativeFeedHasContent(section)) settleNative(section);
        else renderStatus(section, 'Reviews are temporarily unavailable.');
      }
    }, WAIT_MS);
  };

  const initialize = (root = document) => {
    if (root.matches && root.matches('[data-sakura-product-reviews]')) activateFallback(root);
    root.querySelectorAll('[data-sakura-product-reviews]').forEach(activateFallback);
  };

  const destroy = (root) => {
    const sections = [];
    if (root.matches && root.matches('[data-sakura-product-reviews]')) sections.push(root);
    root.querySelectorAll('[data-sakura-product-reviews]').forEach((section) => sections.push(section));
    sections.forEach((section) => {
      stopController(section);
      controllers.delete(section);
    });
  };

  window.SakuraProductReviewsFallback = { initialize, destroy, nativeFeedHasContent };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
